import Vue from 'vue'
import App from './App'
import router from './router'

import BootstrapVue from 'bootstrap-vue'
import VueCryptojs from 'vue-cryptojs'
import Cookies from 'js-cookie'

import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'

import 'vue-search-select/dist/VueSearchSelect.css'

import VueGtag from 'vue-gtag'

import { store } from './store/store.js'

import Users from './Users.js'
import cc64 from './assets/js/cc64.js'

import { library } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/free-solid-svg-icons'
library.add(fas)

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
Vue.component('font-awesome-icon', FontAwesomeIcon)

Vue.use(BootstrapVue)
Vue.use(VueCryptojs)

Vue.config.productionTip = false

Vue.use(
  VueGtag,
  {config: {id: 'G-ZN1D6ZRQHG'}},
  router
)


/*
 * SESSION STORAGE
 */
const SESSION_KEY = 'wynemaker.session'


/*
 * SUPPORTED LANGUAGES
 */
const supportedLanguages = [
  'en',
  'fr',
  'de',
  'es',
  'it',
  'nl',
  'pt'
]


/*
 * LANGUAGE SELECTION THROUGH URL
 *
 * Examples:
 *
 * https://www.wynemaker.com/?lang=fr
 * https://www.wynemaker.com/?lang=nl
 */
const params = new URLSearchParams(window.location.search)

const lang = params.get('lang')

if (lang) {

  const language = lang.toLowerCase()

  if (supportedLanguages.includes(language)) {

    Cookies.set('wynelover.language', language, {
      expires: 365,
      path: '/'
    })

    store.commit('setLanguage', language)

    /*
     * Remove ONLY the lang parameter.
     * Preserve any other query parameters.
     */
    params.delete('lang')

    const remainingQuery = params.toString()

    window.history.replaceState(
      {},
      '',
      window.location.pathname +
      (remainingQuery ? '?' + remainingQuery : '')
    )
  }

} else {

  /*
   * RESTORE LANGUAGE FROM COOKIE
   */
  const language = Cookies.get('wynelover.language')

  if (supportedLanguages.includes(language)) {
    store.commit('setLanguage', language)
  }
}


/*
 * RESTORE USER SESSION
 *
 * sessionStorage contains ONLY:
 *
 * {
 *   user_id: "..."
 * }
 */
async function restoreSession() {
  let local_debug = true

  if (local_debug) console.log("");
  if (local_debug) console.log("main.restoreSession: START");

  let savedSession = null

  try {

    savedSession = sessionStorage.getItem(SESSION_KEY)

  } catch (error) {

    if (local_debug) console.log("main.restoreSession: Cannot access sessionStorage - ", error);
    return
  }


  /*
   * NO SESSION = NORMAL NOT-LOGGED-IN START
   */
  if (!savedSession) {

    if (local_debug) console.log("main.restoreSession: No saved session.");
    if (local_debug) console.log("main.restoreSession: STOP");

    return
  }


  try {

    /*
     * PARSE SAVED SESSION
     */
    const session = JSON.parse(savedSession)

    if (local_debug) console.log("main.restoreSession: Saved session = ", session);


    /*
     * VALIDATE USER ID
     */
    if (
      !session.user_id ||
      typeof session.user_id !== "string" ||
      session.user_id.length !== 24
    ) {

      if (local_debug) console.log("main.restoreSession: Invalid saved user_id.");

      sessionStorage.removeItem(SESSION_KEY)

      return
    }


    const user_id = session.user_id

    if (local_debug) console.log("main.restoreSession: Restoring user_id = ", user_id);


    /*
     * LOAD USER FROM DATABASE
     */
    let user = null

    try {

      user = await Users.getone(user_id)

      if (local_debug) console.log("main.restoreSession: Users.getone successful.");

    } catch (error) {

      if (local_debug) console.log("main.restoreSession: Users.getone FAILED - ", error);

      sessionStorage.removeItem(SESSION_KEY)

      return
    }


    /*
     * VALIDATE USER
     */
    if (
      !user ||
      typeof user !== "object"
    ) {

      if (local_debug) console.log("main.restoreSession: Invalid user returned.");

      sessionStorage.removeItem(SESSION_KEY)

      return
    }


    /*
     * EXISTING APPLICATION CONVENTION:
     * Mongo _id is kept separately in user_id.
     */
    if (user._id) {
      delete user._id
    }


    /*
     * REBUILD PROFILE PICTURE
     */
    let profile_picture = ''

    if (
      Array.isArray(user.image_files) &&
      user.image_files.length > 0 &&
      user.image_files[0] &&
      user.image_files[0].dataURL
    ) {

      try {

        profile_picture = await cc64(
          user.image_files[0].dataURL,
          1,
          1
        )

        if (local_debug) console.log("main.restoreSession: Profile picture successfully restored.");

      } catch (error) {

        if (local_debug) console.log("main.restoreSession: Profile picture restore failed - continuing. Error = ", error);

      }
    }


    /*
     * RESTORE VUEX
     *
     * Set login LAST so components never see
     * inSession=true with an empty user.
     */
    store.commit('setUser', user)
    store.commit('setUserID', user_id)
    store.commit('setProfilePicture', profile_picture)

    store.commit('login')


    if (local_debug) console.log("main.restoreSession: Vuex session successfully restored.");
    if (local_debug) console.log("main.restoreSession: inSession = ", store.state.inSession);
    if (local_debug) console.log("main.restoreSession: user_id = ", store.state.user_id);
    if (local_debug) console.log("main.restoreSession: STOP");
    if (local_debug) console.log("");


  } catch (error) {

    console.log("main.restoreSession: UNEXPECTED ERROR - ", error)

    sessionStorage.removeItem(SESSION_KEY)

    store.commit('resetStore')
  }
}


/*
 * START APPLICATION
 */
async function startApplication() {
  let local_debug = true

  if (local_debug) console.log("main.startApplication: START");

  /*
   * CRITICAL:
   *
   * Restore session BEFORE Vue components are created.
   */
  await restoreSession()

  if (local_debug) console.log("main.startApplication: Creating Vue application.");
  if (local_debug) console.log("main.startApplication: inSession = ", store.state.inSession);
  if (local_debug) console.log("main.startApplication: user_id = ", store.state.user_id);

  new Vue({
    router,
    store,
    render: h => h(App),
  }).$mount('#app')

  if (local_debug) console.log("main.startApplication: STOP");
}


/*
 * DO NOT MOUNT VUE BEFORE THIS POINT
 */
startApplication()